import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/verify-token');
      if (response.data.valid) {
        setUser(response.data.user);
      } else {
        localStorage.removeItem('token');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      console.log('[AUTH] Attempting login for:', email);
      const response = await api.post('/auth/login', { email, password });
      console.log('[AUTH] Login response:', response.data);

      // Only proceed if we have a successful response with a token
      if (response.data && response.data.success && response.data.token) {
        console.log('[AUTH] Login successful, storing token and user');
        localStorage.setItem('token', response.data.token);
        setUser(response.data.user);
        return response.data;
      } else {
        console.error('[AUTH] Invalid response - missing success or token');
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('[AUTH] Login failed:', error.message);
      // Clear any stale auth data
      localStorage.removeItem('token');
      setUser(null);

      // Re-throw the error so Login.jsx can display it
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);

      // Only proceed if we have a successful response with a token
      if (response.data && response.data.success && response.data.token) {
        localStorage.setItem('token', response.data.token);
        setUser(response.data.user);
        return response.data;
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      // Clear any stale auth data
      localStorage.removeItem('token');
      setUser(null);

      // Re-throw the error so Register.jsx can display it
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    window.location.href = '/logout';
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
